/* eslint-disable react/forbid-prop-types */
import PropTypes from 'prop-types';
import {
    Box, Typography, Chip, alpha, IconButton, Tooltip,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { common } from 'src/theme/palette';
import FormSelector from './FormSelector';

/* ──────────────────────────────────────────────────────────────────────────
   SingleFormPicker (FE-7 correction 1)
   ────────────────────────────────────────
   The default Forms section UI for the Send Forms wizard. Optimised for the
   95% case — one form to one patient. Renders the existing `FormSelector`
   (filter dropdown + search + preset dropdown + click-to-select list),
   but treats it as a single-select picker: as soon as a form is chosen,
   we collapse to a single chip with an X to clear the selection.

   To pick more than one form, the practice user expands the Advanced
   accordion below (sequence builder).
   ────────────────────────────────────────────────────────────────────────── */

const TYPE_COLORS = {
    CONSENT: '#078DEE',
    MEDICAL_HISTORY: '#FF5630',
    SURVEY: '#855CF8',
    PATIENT_INTAKE: '#00AB55',
    REFERRAL: '#FFAB00',
    TREATMENT_PLAN: '#FF3030',
    OTHER: '#637381',
};

const SingleFormPicker = ({
    forms,
    formsLoading,
    selectedForms,
    onAddForm,
    onRemoveForm,
    practiceId,
    sequencePresets,
    onApplyPreset,
    onManagePresets,
    onSaveCurrentAsPreset,
}) => {
    // Single-form mode: the first selected form (if any) wins. Extra forms
    // beyond index 0 are only possible via the Advanced sequence builder.
    const primary = selectedForms[0] || null;
    const extraCount = Math.max(0, selectedForms.length - 1);
    const typeColor = primary ? (TYPE_COLORS[primary.form_type] || '#637381') : '#637381';

    // When a form is picked, swap to a single chip output. When the user
    // clears it (X) we drop back to the picker.
    const handleAdd = (form) => {
        // Replace any existing selection so this remains "single-pick".
        if (primary && primary.id !== form.id) {
            onRemoveForm(primary.id);
        }
        onAddForm(form);
    };

    const handleClear = () => {
        if (primary) onRemoveForm(primary.id);
    };

    if (primary && extraCount === 0) {
        // Single chip output — compact, single-form happy path.
        return (
            <Box
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    p: 1.25, borderRadius: '10px',
                    bgcolor: alpha(common.mintyZest, 0.04),
                    border: '1px solid', borderColor: alpha(common.mintyZest, 0.18),
                }}
            >
                <Box sx={{ width: 3, height: 28, borderRadius: 2, bgcolor: typeColor, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        variant="body2"
                        sx={{ fontSize: '13px', fontWeight: 700, color: '#1C252E', lineHeight: 1.3 }}
                        noWrap
                    >
                        {primary.name}
                    </Typography>
                    {primary.sections?.length > 0 && (
                        <Typography sx={{ fontSize: '11px', color: '#919EAB' }}>
                            {primary.sections.length} section{primary.sections.length !== 1 ? 's' : ''}
                        </Typography>
                    )}
                </Box>
                <Chip
                    size="small"
                    label="Selected"
                    sx={{
                        height: 20, fontSize: '10px', fontWeight: 700,
                        bgcolor: alpha(common.mintyZest, 0.12),
                        color: common.mintyZest,
                        textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}
                />
                <Tooltip title="Change form" placement="top" arrow>
                    <IconButton
                        size="small"
                        aria-label="Clear selected form"
                        onClick={handleClear}
                        sx={{
                            width: 24, height: 24, color: '#919EAB',
                            '&:hover': { color: '#FF5630', bgcolor: alpha('#FF5630', 0.06) },
                        }}
                    >
                        <Iconify icon="eva:close-fill" width={14} />
                    </IconButton>
                </Tooltip>
            </Box>
        );
    }

    // Either nothing picked yet, or the user has built a multi-form
    // sequence in Advanced — in the latter case we still show the picker
    // but the AdvancedReveal mirrors the same forms.
    return (
        <Box>
            {extraCount > 0 && (
                <Box
                    sx={{
                        mb: 1, p: 1, borderRadius: '8px',
                        bgcolor: alpha('#078DEE', 0.04),
                        border: '1px solid', borderColor: alpha('#078DEE', 0.16),
                        display: 'flex', alignItems: 'center', gap: 0.75,
                    }}
                >
                    <Iconify icon="solar:playlist-minimalistic-bold-duotone" width={14} style={{ color: '#078DEE' }} />
                    <Typography sx={{ fontSize: '11.5px', color: '#078DEE', fontWeight: 600 }}>
                        Sequence of {selectedForms.length} forms — edit in Advanced
                    </Typography>
                </Box>
            )}
            <FormSelector
                forms={forms}
                formsLoading={formsLoading}
                selectedForms={selectedForms}
                onAdd={handleAdd}
                onRemove={onRemoveForm}
                practiceId={practiceId}
                sequencePresets={sequencePresets}
                onApplyPreset={onApplyPreset}
                onManagePresets={onManagePresets}
                onSaveCurrentAsPreset={onSaveCurrentAsPreset}
            />
        </Box>
    );
};

SingleFormPicker.propTypes = {
    forms: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    formsLoading: PropTypes.bool,
    selectedForms: PropTypes.array.isRequired,
    onAddForm: PropTypes.func.isRequired,
    onRemoveForm: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    sequencePresets: PropTypes.array,
    onApplyPreset: PropTypes.func,
    onManagePresets: PropTypes.func,
    onSaveCurrentAsPreset: PropTypes.func,
};

SingleFormPicker.defaultProps = {
    forms: null,
    formsLoading: false,
    sequencePresets: [],
    onApplyPreset: () => {},
    onManagePresets: () => {},
    onSaveCurrentAsPreset: () => {},
};

export default SingleFormPicker;
